import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Trash2, Loader, AlertCircle, CheckCircle, Lock } from 'lucide-react';
import { paymentPlansApi, installmentSettingsApi } from '../api/client';
import { useTranslation } from '../i18n/useTranslation';

const PAYMENT_METHODS = [
  { value: 'PIX', label: 'PIX' },
  { value: 'CREDIT_CARD', label: 'Cartão de Crédito' },
];

const PaymentPlanEdit = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [plan, setPlan] = useState(null);
  const [paidParts, setPaidParts] = useState([]);
  const [unpaidParts, setUnpaidParts] = useState([]);
  const [newParts, setNewParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Installment settings from database
  const [installmentSettings, setInstallmentSettings] = useState(null);
  const [maxInstallmentsNoInterest, setMaxInstallmentsNoInterest] = useState(6);
  const [interestRate, setInterestRate] = useState(2.99);
  const [maxInstallments, setMaxInstallments] = useState(24);
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    loadInstallmentSettings();
    loadPlan();
  }, [planId]);

  const loadInstallmentSettings = async () => {
    try {
      const response = await installmentSettingsApi.getActive();
      if (response.data.success && response.data.data) {
        const settings = response.data.data;
        setInstallmentSettings(settings);
        setMaxInstallmentsNoInterest(settings.interest_free_limit || 6);
        setMaxInstallments(settings.max_installments || 24);
        setTiers(settings.tiers || []);
        
        // Calculate interest rate from first tier with interest
        if (settings.tiers && settings.tiers.length > 0) {
          const firstInterestTier = settings.tiers.find(t => t.interestRate > 0);
          if (firstInterestTier) {
            setInterestRate(parseFloat(firstInterestTier.interestRate));
          }
        }
      }
    } catch (err) {
      console.error('Failed to load installment settings, using defaults:', err);
    }
  };

  const loadPlan = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await paymentPlansApi.getById(planId);
      
      if (response.data.success) {
        const responseData = response.data.data;
        
        // The API returns plan data with parts array directly in the response
        const { parts = [], ...planData } = responseData;
        
        setPlan(planData);
        
        const paid = parts.filter(p => p.status === 'paid');
        const unpaid = parts.filter(p => p.status === 'pending' || p.status === 'failed');
        
        setPaidParts(paid);
        setUnpaidParts(unpaid);
        
        // Initialize new parts with unpaid parts data
        if (unpaid.length > 0) {
          setNewParts(unpaid.map(p => ({
            method: p.method,
            amount: p.amount,
            installments: p.installments || '',
            label: p.label || '',
          })));
        } else {
          // If no unpaid parts, start with one empty part
          setNewParts([{ method: 'PIX', amount: '', installments: '', label: '' }]);
        }
      } else {
        setError('Plano de pagamento não encontrado');
      }
    } catch (err) {
      console.error('Load Plan Error:', err);
      setError(err.response?.data?.error || 'Erro ao carregar plano de pagamento');
    } finally {
      setLoading(false);
    }
  };

  const addPart = () => {
    setNewParts([...newParts, { method: 'PIX', amount: '', installments: '', label: '' }]);
  };

  const removePart = (index) => {
    setNewParts(newParts.filter((_, i) => i !== index));
  };

  const updatePart = (index, field, value) => {
    const updated = [...newParts];
    updated[index] = { ...updated[index], [field]: value };
    setNewParts(updated);
  };

  const calculateTotal = () => {
    return newParts.reduce((sum, part) => {
      const amount = parseFloat(part.amount) || 0;
      return sum + amount;
    }, 0);
  };

  const getTotalPaid = () => {
    return paidParts.reduce((sum, part) => {
      return sum + parseFloat(part.paid_amount || part.amount);
    }, 0);
  };

  const getRemainingAmount = () => {
    if (!plan) return 0;
    return parseFloat(plan.order_total) - getTotalPaid() - calculateTotal();
  };

  const calculateFinalAmount = (part) => {
    const amount = parseFloat(part.amount) || 0;
    if (part.method !== 'CREDIT_CARD' || !part.installments) {
      return amount;
    }
    
    const installments = parseInt(part.installments);
    
    // Use tier-based calculation if available
    if (tiers && tiers.length > 0) {
      const tier = tiers.find(
        t => installments >= t.minInstallments && installments <= t.maxInstallments
      );
      
      if (tier && tier.interestRate > 0) {
        const rate = parseFloat(tier.interestRate) / 100;
        const finalAmount = amount * Math.pow(1 + rate, installments);
        return finalAmount;
      }
      return amount; // No interest for this tier
    }
    
    // Fallback to simple calculation
    if (installments <= maxInstallmentsNoInterest) {
      return amount;
    }
    
    const rate = interestRate / 100;
    const finalAmount = amount * Math.pow(1 + rate, installments - maxInstallmentsNoInterest);
    return finalAmount;
  };

  const calculateInstallmentValue = (part) => {
    const finalAmount = calculateFinalAmount(part);
    const installments = parseInt(part.installments) || 1;
    return finalAmount / installments;
  };

  const isValidForSubmission = () => {
    if (!plan) return false;
    
    const allPartsValid = newParts.every(part => {
      if (!part.method || !part.amount) return false;
      if (parseFloat(part.amount) <= 0) return false;
      if (part.method === 'CREDIT_CARD' && !part.installments) return false;
      return true;
    });

    if (!allPartsValid) return false;

    const remaining = getRemainingAmount();
    return Math.abs(remaining) < 0.01;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('=== FORM SUBMISSION DEBUG ===');
    console.log('Plan:', plan);
    console.log('New Parts:', newParts);
    console.log('Paid Parts:', paidParts);
    console.log('Total Paid:', getTotalPaid());
    console.log('New Total:', calculateTotal());
    console.log('Remaining:', getRemainingAmount());
    console.log('Is Valid:', isValidForSubmission());
    
    if (!isValidForSubmission()) {
      const validationErrors = [];
      
      newParts.forEach((part, i) => {
        if (!part.method) validationErrors.push(`Parte ${i + 1}: método não selecionado`);
        if (!part.amount || parseFloat(part.amount) <= 0) validationErrors.push(`Parte ${i + 1}: valor inválido`);
        if (part.method === 'CREDIT_CARD' && !part.installments) validationErrors.push(`Parte ${i + 1}: parcelas não definidas`);
      });
      
      const remaining = getRemainingAmount();
      if (Math.abs(remaining) >= 0.01) {
        validationErrors.push(`Total não corresponde ao pedido. Faltam: R$ ${remaining.toFixed(2)}`);
      }
      
      const errorMsg = validationErrors.length > 0 
        ? validationErrors.join('; ')
        : 'Por favor, preencha todos os campos corretamente e certifique-se de que o total corresponde ao valor do pedido.';
      
      console.error('Validation failed:', validationErrors);
      setError(errorMsg);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      console.log('Submitting edit request...');
      const response = await paymentPlansApi.edit(planId, { parts: newParts });
      console.log('Edit response:', response.data);

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/payment-history');
        }, 2000);
      } else {
        setError('Falha ao editar plano de pagamento');
      }
    } catch (err) {
      console.error('Edit error:', err);
      setError(err.response?.data?.error || 'Erro ao editar plano de pagamento');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Plano de pagamento não encontrado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Editar Plano de Pagamento</h1>
        <p className="mt-2 text-sm text-gray-600">
          Pedido #{plan.order_number} - {plan.customer_name}
        </p>
        <p className="text-sm text-gray-600">
          Total do Pedido: {formatCurrency(plan.order_total)}
        </p>
      </div>

      {paidParts.length > 0 && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">Editando Plano Parcialmente Pago</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Pagamentos já realizados não podem ser alterados</li>
                <li>Você pode editar apenas as partes ainda não pagas</li>
                <li>O total das novas partes deve completar o valor restante do pedido</li>
                <li>Partes não pagas anteriores serão canceladas e substituídas</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-800">Plano editado com sucesso! Redirecionando...</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Paid Parts - Read Only */}
        {paidParts.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-5 h-5 text-green-700" />
              <h2 className="text-lg font-semibold text-green-900">Pagamentos Realizados</h2>
            </div>
            <div className="space-y-3">
              {paidParts.map((part, index) => (
                <div key={part.id} className="bg-white rounded-lg p-4 border border-green-300">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs font-medium text-gray-500">Método</p>
                      <p className="text-sm font-semibold text-gray-900">{part.method}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500">Valor Pago</p>
                      <p className="text-sm font-semibold text-green-700">
                        {formatCurrency(part.paid_amount || part.amount)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500">Status</p>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Pago
                      </span>
                    </div>
                  </div>
                  {part.label && (
                    <p className="mt-2 text-xs text-gray-600">{part.label}</p>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-green-300">
              <p className="text-sm font-semibold text-green-900">
                Total Pago: {formatCurrency(getTotalPaid())}
              </p>
            </div>
          </div>
        )}

        {/* New Parts - Editable */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {unpaidParts.length > 0 ? 'Editar Pagamentos Pendentes' : 'Novos Pagamentos'}
            </h2>
            <button
              type="button"
              onClick={addPart}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Adicionar Parte
            </button>
          </div>

          {newParts.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">
              Nenhuma parte de pagamento. Clique em "Adicionar Parte" para começar.
            </p>
          ) : (
            <div className="space-y-4">
              {newParts.map((part, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-gray-700">Parte {index + 1}</h3>
                    {newParts.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePart(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Método de Pagamento
                      </label>
                      <select
                        value={part.method}
                        onChange={(e) => updatePart(index, 'method', e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      >
                        {PAYMENT_METHODS.map(method => (
                          <option key={method.value} value={method.value}>
                            {method.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Valor
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={part.amount}
                        onChange={(e) => updatePart(index, 'amount', e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="0.00"
                      />
                    </div>

                    {part.method === 'CREDIT_CARD' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Parcelas
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="24"
                          value={part.installments}
                          onChange={(e) => updatePart(index, 'installments', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          placeholder="1"
                        />
                        {part.installments && part.amount && (
                          <div className="mt-2 p-2 bg-blue-50 rounded-md">
                            <p className="text-xs font-medium text-blue-900">
                              {parseInt(part.installments)}x de R$ {calculateInstallmentValue(part).toFixed(2)}
                            </p>
                            {parseInt(part.installments) > maxInstallmentsNoInterest && (
                              <p className="text-xs text-blue-700 mt-1">
                                Total com juros: R$ {calculateFinalAmount(part).toFixed(2)}
                                <span className="text-blue-600"> ({interestRate}% a.m.)</span>
                              </p>
                            )}
                            {parseInt(part.installments) <= maxInstallmentsNoInterest && (
                              <p className="text-xs text-green-700 mt-1">
                                Sem juros
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Descrição
                      </label>
                      <input
                        type="text"
                        value={part.label}
                        onChange={(e) => updatePart(index, 'label', e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="ex: Cartão 1, PIX"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Resumo</h2>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total do Pedido:</span>
              <span className="font-medium text-gray-900">{formatCurrency(plan.order_total)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Já Pago:</span>
              <span className="font-medium text-green-700">{formatCurrency(getTotalPaid())}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Novos Pagamentos:</span>
              <span className="font-medium text-blue-700">{formatCurrency(calculateTotal())}</span>
            </div>
            <div className="pt-2 border-t border-gray-300">
              <div className="flex justify-between">
                <span className="text-sm font-semibold text-gray-900">Restante:</span>
                <span className={`text-sm font-bold ${
                  Math.abs(getRemainingAmount()) < 0.01 
                    ? 'text-green-600' 
                    : 'text-red-600'
                }`}>
                  {formatCurrency(getRemainingAmount())}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate('/payment-history')}
            className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!isValidForSubmission() || submitting}
            className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Salvando...
              </>
            ) : (
              'Salvar Alterações'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PaymentPlanEdit;
